/**
 * Attachment list + "Add" button for travel items and events.
 * Uses the shared `VaultFilePickerDialog` for both browse-existing
 * and upload-new flows.
 */

import { useState } from 'react';
import { Paperclip, FileText, Image, Film, Music, File, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { VaultFilePickerDialog, type VaultPickerResult } from '@/components/VaultFilePickerDialog';
import { toast } from '@/components/ui/use-toast';
import { captureError } from '@/lib/telemetry';
import api from '@/lib/api';
import type { TravelAttachmentResponse } from '../api';

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getFileIcon(mimeType?: string) {
  if (!mimeType) return File;
  if (mimeType.startsWith('image/')) return Image;
  if (mimeType.startsWith('video/')) return Film;
  if (mimeType.startsWith('audio/')) return Music;
  if (mimeType.startsWith('text/') || mimeType.includes('pdf') || mimeType.includes('document'))
    return FileText;
  return File;
}

interface TravelAttachmentsProps {
  attachments: TravelAttachmentResponse[];
  onAddAttachment: (fileId: string) => Promise<unknown>;
  onRemoveAttachment: (attachmentId: string, documentId: string) => Promise<unknown>;
}

export function TravelAttachments({
  attachments,
  onAddAttachment,
  onRemoveAttachment,
}: TravelAttachmentsProps) {
  const [showPicker, setShowPicker] = useState(false);

  const handlePicked = async (file: VaultPickerResult) => {
    try {
      await onAddAttachment(file.documentId);
    } catch (err) {
      captureError(err instanceof Error ? err : new Error(String(err)), {
        component: 'TravelAttachments',
        action: 'attach',
      });
      toast({
        title: 'Failed to attach file',
        description: err instanceof Error ? err.message : 'An error occurred',
        variant: 'destructive',
      });
    }
  };

  const handleDownload = async (attachment: TravelAttachmentResponse) => {
    const { url } = await api.getVaultDocumentDownloadUrl(attachment.fileId);
    window.open(url, '_blank');
  };

  return (
    <div className="space-y-1.5">
      {/* Existing attachments */}
      {attachments.length > 0 && (
        <div className="space-y-1">
          {attachments.map((att) => {
            const Icon = getFileIcon(att.mimeType);
            return (
              <div
                key={att.attachmentId}
                className="group flex items-center gap-2 rounded border border-border px-2 py-1.5 text-sm hover:bg-accent/50 transition-colors"
              >
                <Icon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                <button
                  className="truncate text-left hover:underline flex-1 min-w-0"
                  onClick={() => handleDownload(att)}
                >
                  {att.fileName || att.fileId}
                </button>
                {att.fileSize != null && (
                  <span className="text-xs text-muted-foreground shrink-0">
                    {formatFileSize(att.fileSize)}
                  </span>
                )}
                <button
                  onClick={() => onRemoveAttachment(att.attachmentId, att.fileId)}
                  className="p-0.5 rounded hover:bg-muted text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Add attachment button — opens shared picker (browse + upload). */}
      <Button
        variant="ghost"
        size="sm"
        className="h-7 text-xs text-muted-foreground"
        onClick={() => setShowPicker(true)}
      >
        <Paperclip className="h-3 w-3 mr-1" /> Attach file
      </Button>

      <VaultFilePickerDialog
        open={showPicker}
        onOpenChange={setShowPicker}
        title="Attach a File"
        description="Pick a file from your Vault, or upload a new one to attach."
        confirmLabel="Attach"
        onFileSelected={handlePicked}
      />
    </div>
  );
}
